import dayjs, { type Dayjs } from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'

// types
import type { CalendarDropTarget, CalendarEvent, CalendarWeekStart } from '@/types/components/calendar'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(isoWeek)

/** Anything the calendar can normalise into a Dayjs */
export type CalendarDateInput = string | number | Date | Dayjs | null | undefined

/** Fallback length for events that have no end, in minutes */
export const DEFAULT_EVENT_DURATION_MINUTES = 30

/** Minutes in a day, used to clamp day-relative offsets */
export const MINUTES_PER_DAY = 1440

/**
 * Normalises any supported input into a Dayjs, optionally in a specific IANA timezone.
 * @param value - Raw date value
 * @param zone - IANA timezone name, e.g. 'Europe/London'
 * @returns A valid Dayjs, or null when the input is empty or unparseable
 */
export function toCalendarDate(value: CalendarDateInput, zone?: string): Dayjs | null {
  // Nothing to parse
  if (value === null || value === undefined || value === '') {
    return null
  }

  const parsed = zone ? dayjs.tz(value as string | number | Date, zone) : dayjs(value)

  return parsed.isValid() ? parsed : null
}

/**
 * Resolves an event's start, preferring the event's own timezone over the calendar's.
 * @param event - Event to read
 * @param zone - Calendar-level fallback timezone
 * @returns Start as a Dayjs, or null when the event has no parseable start
 */
export function getEventStart(event: CalendarEvent, zone?: string): Dayjs | null {
  return toCalendarDate(event.start, event.timezone ?? zone)
}

/**
 * Resolves an event's end, falling back to start + `defaultDurationMinutes`.
 * Also guarantees the end is strictly after the start so zero-length events stay visible.
 * @param event - Event to read
 * @param zone - Calendar-level fallback timezone
 * @param defaultDurationMinutes - Length applied when the event has no end
 * @returns End as a Dayjs, or null when the event has no parseable start
 */
export function getEventEnd(
  event: CalendarEvent,
  zone?: string,
  defaultDurationMinutes: number = DEFAULT_EVENT_DURATION_MINUTES
): Dayjs | null {
  const start = getEventStart(event, zone)

  // Without a start there is nothing to anchor the end to
  if (!start) {
    return null
  }

  const end = toCalendarDate(event.end, event.timezone ?? zone)

  // Missing or non-positive ends get the default duration
  if (!end || !end.isAfter(start)) {
    return start.add(defaultDurationMinutes, 'minute')
  }

  return end
}

/**
 * Whether two half-open ranges [aStart, aEnd) and [bStart, bEnd) share any time.
 * @param aStart - First range start
 * @param aEnd - First range end
 * @param bStart - Second range start
 * @param bEnd - Second range end
 */
export function rangesOverlap(aStart: Dayjs, aEnd: Dayjs, bStart: Dayjs, bEnd: Dayjs): boolean {
  return aStart.isBefore(bEnd) && bStart.isBefore(aEnd)
}

/**
 * Start of the week containing a date, honouring a configurable first day.
 * @param date - Any date within the week
 * @param weekStart - 0 = Sunday through 6 = Saturday
 */
export function startOfWeek(date: Dayjs, weekStart: CalendarWeekStart = 1): Dayjs {
  const offset = (date.day() - weekStart + 7) % 7

  return date.startOf('day').subtract(offset, 'day')
}

/**
 * End of the week containing a date, as the start of the following week (exclusive).
 * @param date - Any date within the week
 * @param weekStart - 0 = Sunday through 6 = Saturday
 */
export function endOfWeek(date: Dayjs, weekStart: CalendarWeekStart = 1): Dayjs {
  return startOfWeek(date, weekStart).add(7, 'day')
}

/**
 * Minutes elapsed between the start of a reference day and a moment.
 * Values may fall outside 0-1440 when the moment is on another day.
 * @param date - Moment to measure
 * @param dayStart - Start of the reference day
 */
export function minutesFromDayStart(date: Dayjs, dayStart: Dayjs): number {
  return date.diff(dayStart.startOf('day'), 'minute')
}

/**
 * Whether a date falls on a Saturday or Sunday.
 * @param date - Date to test
 */
export function isWeekend(date: Dayjs): boolean {
  const day = date.day()

  return day === 0 || day === 6
}

/**
 * Events overlapping a given day, in render order.
 * @param events - Events to filter
 * @param day - Day to test against
 * @param zone - Calendar-level fallback timezone
 * @returns Matching events sorted by `sortCalendarEvents`
 */
export function getEventsForDay(events: CalendarEvent[], day: Dayjs, zone?: string): CalendarEvent[] {
  const dayStart = day.startOf('day')
  const dayEnd = dayStart.add(1, 'day')

  const matching = events.filter(event => {
    const start = getEventStart(event, zone)

    // Unparseable events are dropped rather than rendered at epoch
    if (!start) {
      return false
    }

    // All-day events are compared on calendar days, ignoring any time component
    if (event.allDay) {
      const end = toCalendarDate(event.end, event.timezone ?? zone) ?? start

      return !start.isAfter(dayStart, 'day') && !end.isBefore(dayStart, 'day')
    }

    const end = getEventEnd(event, zone)

    return end !== null && rangesOverlap(start, end, dayStart, dayEnd)
  })

  return sortCalendarEvents(matching, zone)
}

/**
 * Sorts events for display: all-day first, then by start, then by title.
 * Returns a new array rather than sorting in place.
 * @param events - Events to sort
 * @param zone - Calendar-level fallback timezone
 */
export function sortCalendarEvents(events: CalendarEvent[], zone?: string): CalendarEvent[] {
  return [...events].sort((left, right) => {
    // All-day events sit above timed events, matching Google Calendar
    if (Boolean(left.allDay) !== Boolean(right.allDay)) {
      return left.allDay ? -1 : 1
    }

    const leftStart = getEventStart(left, zone)
    const rightStart = getEventStart(right, zone)

    // Push unparseable events to the end rather than throwing
    if (!leftStart || !rightStart) {
      return leftStart ? -1 : 1
    }

    const startDelta = leftStart.valueOf() - rightStart.valueOf()

    // Equal starts fall back to a stable alphabetical order
    if (startDelta !== 0) {
      return startDelta
    }

    return left.title.localeCompare(right.title)
  })
}

/**
 * Moves an event onto a drop target, keeping its duration.
 * Month cells preserve the original time of day (and all-day flag); the all-day row
 * converts the event to all-day; a timed column places it at the snapped minute.
 * @param event - Event being moved
 * @param target - Drop location
 * @param zone - Calendar-level fallback timezone
 */
export function moveCalendarEvent(event: CalendarEvent, target: CalendarDropTarget, zone?: string): CalendarEvent {
  const start = getEventStart(event, zone)
  const end = getEventEnd(event, zone)
  const durationMinutes =
    start && end ? Math.max(end.diff(start, 'minute'), DEFAULT_EVENT_DURATION_MINUTES) : DEFAULT_EVENT_DURATION_MINUTES
  const day = toCalendarDate(target.date, zone)?.startOf('day')

  // An unparseable target date leaves the event where it is
  if (!day) {
    return event
  }

  /**
   * Length of an all-day event in whole days, always at least one.
   */
  function allDayLength(): number {
    // Timed events dropped onto the all-day row become a single day
    if (!event.allDay || !start || !end) {
      return 1
    }

    return Math.max(end.startOf('day').diff(start.startOf('day'), 'day'), 1)
  }

  // Dropping onto the all-day row converts the event to all-day
  if (target.allDay) {
    return {
      ...event,
      allDay: true,
      start: day.format('YYYY-MM-DD'),
      end: day.add(allDayLength(), 'day').format('YYYY-MM-DD')
    }
  }

  // Month cells keep the original clock time (or stay all-day)
  if (target.minutes === null) {
    // All-day events stay all-day on the new date
    if (event.allDay) {
      return {
        ...event,
        allDay: true,
        start: day.format('YYYY-MM-DD'),
        end: day.add(allDayLength(), 'day').format('YYYY-MM-DD')
      }
    }

    const timeMinutes = start ? start.hour() * 60 + start.minute() : 9 * 60
    const newStart = day.add(timeMinutes, 'minute')

    return {
      ...event,
      allDay: false,
      start: newStart.toISOString(),
      end: newStart.add(durationMinutes, 'minute').toISOString()
    }
  }

  const newStart = day.add(target.minutes, 'minute')

  return {
    ...event,
    allDay: false,
    start: newStart.toISOString(),
    end: newStart.add(durationMinutes, 'minute').toISOString()
  }
}
